/**
 * useOfflineSync — Manages TourScan offline scan queue.
 *
 * - Detects online/offline status via navigator.onLine + events
 * - When offline: scan calls are queued in IndexedDB
 * - When back online: queued scans are flushed to the API automatically
 * - Returns helpers to enqueue scans and check sync status
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { tourscanApi } from '@/api/tourscan';
import { enqueueScan, getAllPending, deletePendingScan, incrementRetries, getPendingCount, } from '@/lib/tourscan-offline';
const MAX_RETRIES = 3;
export function useOfflineSync({ tenantId, sessionId, onSyncSuccess, onSyncError, }) {
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [pendingCount, setPendingCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const syncingRef = useRef(false);
    // Refresh pending count
    const refreshCount = useCallback(async () => {
        if (!tenantId || !sessionId)
            return;
        const count = await getPendingCount(tenantId, sessionId);
        setPendingCount(count);
    }, [tenantId, sessionId]);
    // Listen to online/offline events
    useEffect(() => {
        const onOnline = () => {
            setIsOffline(false);
        };
        const onOffline = () => {
            setIsOffline(true);
        };
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, []);
    // Refresh count when session changes
    useEffect(() => {
        refreshCount();
    }, [refreshCount]);
    // Auto-sync when we come back online
    useEffect(() => {
        if (!isOffline && pendingCount > 0) {
            syncNow();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOffline]);
    const syncNow = useCallback(async () => {
        if (syncingRef.current || !tenantId || !navigator.onLine)
            return;
        syncingRef.current = true;
        setIsSyncing(true);
        try {
            const pending = await getAllPending(tenantId);
            if (pending.length === 0)
                return;
            let synced = 0;
            for (const item of pending) {
                if (item.retries >= MAX_RETRIES) {
                    // Give up on items that failed too many times
                    await deletePendingScan(tenantId, item.id);
                    continue;
                }
                try {
                    await tourscanApi.recordAttendance({
                        qrCode: item.qrCode,
                        sessionId: item.sessionId,
                        status: item.status,
                    });
                    await deletePendingScan(tenantId, item.id);
                    synced++;
                }
                catch (err) {
                    const status = err?.response?.status;
                    if (status === 409) {
                        // Already recorded (duplicate) — remove from queue silently
                        await deletePendingScan(tenantId, item.id);
                    }
                    else {
                        await incrementRetries(tenantId, item.id);
                    }
                }
            }
            if (synced > 0)
                onSyncSuccess?.(synced);
            await refreshCount();
        }
        catch {
            onSyncError?.('Error al sincronizar escaneos pendientes');
        }
        finally {
            syncingRef.current = false;
            setIsSyncing(false);
        }
    }, [tenantId, onSyncSuccess, onSyncError, refreshCount]);
    /**
     * If online: call sendFn directly.
     * If offline: enqueue the scan in IndexedDB.
     */
    const queueOrSend = useCallback(async (qrCode, sendFn) => {
        if (navigator.onLine) {
            await sendFn();
            return { queued: false };
        }
        else {
            if (!tenantId || !sessionId)
                throw new Error('No hay sesión activa');
            await enqueueScan(tenantId, sessionId, qrCode);
            await refreshCount();
            return { queued: true };
        }
    }, [tenantId, sessionId, refreshCount]);
    return { isOffline, pendingCount, isSyncing, syncNow, queueOrSend };
}
//# sourceMappingURL=useOfflineSync.js.map